class ArtCartPopup {
  constructor(el) {
    this.el = el;
    this.freeShippingThreshold = parseInt(el.dataset.freeShippingThreshold || '0', 10);

    this.state = { lineKey: null, qty: 1 };

    this._bindClose();
    this._bindQty();
    this._suppressBuiltinNotification();
    this._subscribeToCartUpdate();
  }

  /* ── Setup ─────────────────────────────────────────── */

  _bindClose() {
    this.el.querySelector('#acpop-close').addEventListener('click', () => this.close());
    this.el.querySelector('#acpop-continue').addEventListener('click', () => this.close());

    // Click on overlay backdrop (anything outside .acpop closes it)
    this.el.addEventListener('click', (e) => {
      if (!e.target.closest('.acpop')) this.close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
  }

  _bindQty() {
    this.el.querySelector('#acpop-qty-minus').addEventListener('click', () => this._changeQty(-1));
    this.el.querySelector('#acpop-qty-plus').addEventListener('click', () => this._changeQty(1));
  }

  // Suppress the built-in cart-notification open so it doesn't compete with our popup
  _suppressBuiltinNotification() {
    const notif = document.querySelector('cart-notification');
    if (notif) notif.open = function () {};
  }

  _subscribeToCartUpdate() {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== 'product-form') return;
      this._onProductAdded(event.cartData);
    });
  }

  /* ── Cart add handler ──────────────────────────────── */

  _onProductAdded(cartData) {
    // cartData is the /cart/add.js response for the added item
    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        // Find the matching line item by variant_id to get the line key
        const lineItem = cart.items.find((i) => i.variant_id === (cartData.variant_id || cartData.id));

        this.state.lineKey = lineItem ? lineItem.key : cartData.key;
        this.state.qty = lineItem ? lineItem.quantity : (cartData.quantity || 1);

        this._renderProduct(cartData, lineItem);
        this._renderSummary(cart);
        this._loadComplementary(cartData);
        this.open();
      })
      .catch(console.error);
  }

  /* ── Render product (left column) ─────────────────── */

  _renderProduct(item, lineItem) {
    const img = this.el.querySelector('#acpop-img');
    img.src = (item.featured_image && item.featured_image.url) || item.image || '';
    img.alt = item.product_title || item.title || '';

    this.el.querySelector('#acpop-vendor').textContent = item.vendor || '';

    this.el.querySelector('#acpop-title').textContent = item.product_title || item.title || '';

    // Variant options — filter out "Default Title" (Shopify's placeholder)
    const optLines = (item.options_with_values || [])
      .filter((o) => o.value !== 'Default Title')
      .map((o) => `${o.name}: ${o.value}`)
      .join('\n');
    const optEl = this.el.querySelector('#acpop-options');
    optEl.textContent = optLines;
    optEl.hidden = !optLines;

    this.el.querySelector('#acpop-price').textContent = this._money(item.final_price || item.price || 0);

    this.el.querySelector('#acpop-qty').textContent = this.state.qty;
    this._syncMinusBtn();
  }

  /* ── Render cart summary (right column) ───────────── */

  _renderSummary(cart) {
    this.el.querySelector('#acpop-total').textContent = this._money(cart.total_price);

    const shippingEl = this.el.querySelector('#acpop-shipping');
    if (this.freeShippingThreshold > 0 && cart.total_price >= this.freeShippingThreshold) {
      shippingEl.removeAttribute('hidden');
    } else {
      shippingEl.setAttribute('hidden', '');
    }
  }

  /* ── Complementary products via Section Rendering API */

  _loadComplementary(item) {
    const container = this.el.querySelector('#acpop-complementary');
    const accContainer = this.el.querySelector('#acpop-accessories');
    const header = this.el.querySelector('.acpop__comp-header');
    const accHeader = this.el.querySelector('.acpop__acc-header');

    container.innerHTML = '';
    if (accContainer) accContainer.innerHTML = '';
    if (header) header.setAttribute('hidden', '');
    if (accHeader) accHeader.setAttribute('hidden', '');

    const handle = this._handleFromUrl(item.url || '');
    if (!handle) return;

    const variantId = item.variant_id || item.id;
    // PDP Complementary now lives inline in the main-product section, so fetch the full
    // product page rather than section_id=main: the Section Rendering API needs the
    // compound section id (template--<id>__main), which differs per product template and
    // can't be hardcoded here. The .acp-section markup is extracted below regardless of
    // what else is on the page.
    const url = `/products/${handle}?variant=${variantId}`;

    fetch(url)
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const sections = doc.querySelectorAll('.acp-section');
        if (!sections.length) return;

        sections.forEach((s) => {
          if (s.classList.contains('acp-section--accessories') && accContainer) {
            accContainer.appendChild(document.adoptNode(s));
            if (accHeader) accHeader.removeAttribute('hidden');
          } else {
            container.appendChild(document.adoptNode(s));
            if (header) header.removeAttribute('hidden');
          }
        });

        // Re-execute inline scripts (adoptNode skips script execution)
        [container, accContainer].forEach((c) => {
          if (!c) return;
          c.querySelectorAll('script').forEach((oldScript) => {
            const s = document.createElement('script');
            s.textContent = oldScript.textContent;
            oldScript.replaceWith(s);
          });
        });
      })
      .catch(console.error);
  }

  /* ── Quantity controls ─────────────────────────────── */

  _changeQty(delta) {
    const newQty = Math.max(0, this.state.qty + delta);

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: this.state.lineKey, quantity: newQty }),
    })
      .then((r) => r.json())
      .then((cart) => {
        if (newQty === 0) {
          this.close();
          return;
        }
        this.state.qty = newQty;
        this.el.querySelector('#acpop-qty').textContent = newQty;
        this._syncMinusBtn();
        this._renderSummary(cart);

        // Let the theme update the cart icon bubble
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'art-cart-popup', cartData: cart });
      })
      .catch(console.error);
  }

  _syncMinusBtn() {
    this.el.querySelector('#acpop-qty-minus').disabled = this.state.qty <= 1;
  }

  /* ── Open / close ──────────────────────────────────── */

  open() {
    this.el.removeAttribute('aria-hidden');
    document.body.classList.add('acpop-open');
  }

  close() {
    this.el.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('acpop-open');
  }

  /* ── Helpers ───────────────────────────────────────── */

  _handleFromUrl(url) {
    const m = url.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  _money(cents) {
    return (
      new Intl.NumberFormat('pl-PL', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(cents / 100) + ' zł'
    );
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const el = document.getElementById('art-cart-popup');
  if (el) new ArtCartPopup(el);
});
